import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useUpdateInvoice, useDeleteInvoice } from '../hooks/useInvoices'
import { useCreatePayment, useDeletePayment } from '@/features/payments/hooks/usePayments'
import { InvoiceForm } from './InvoiceForm'
import { PaymentForm } from '@/features/payments/components/PaymentForm'
import { Modal } from '@/components/ui/Modal'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { AgingBadge } from './AgingBadge'
import { Edit, Trash2, Plus, Phone, Mail, MapPin, User } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/format'
import { calculateDaysPastDue, formatAging } from '@/utils/aging'

interface InvoiceDetailModalProps {
  invoice: any
  payments?: any[]
  isOpen: boolean
  onClose: () => void
}

export function InvoiceDetailModal({ invoice, payments = [], isOpen, onClose }: InvoiceDetailModalProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const updateInvoice = useUpdateInvoice()
  const deleteInvoice = useDeleteInvoice()
  const createPayment = useCreatePayment()
  const deletePayment = useDeletePayment()

  // Calculate aging at read time (never use stale days_past_due from database)
  const daysPastDue = calculateDaysPastDue({
    due_date: invoice.due_date,
    status: invoice.status,
    balance_due: invoice.balance_due,
  })

  const handleUpdate = async (data: any) => {
    await updateInvoice.mutateAsync({ id: invoice.id, updates: data })
    setIsEditing(false)
  }

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete invoice ${invoice.invoice_number}? This action cannot be undone.`)) {
      return
    }

    await deleteInvoice.mutateAsync(invoice.id)
    onClose()
  }

  const handleCreatePayment = async (data: any) => {
    // Include the invoice_id in the payment
    const paymentData = {
      ...data,
      invoice_ids: [invoice.id],
    }

    await createPayment.mutateAsync(paymentData)
    setShowPaymentForm(false)
    // Close the invoice modal to force refresh with updated data
    onClose()
  }

  const handleDeletePayment = async (paymentId: string, paymentAmount: number) => {
    if (!confirm(`Are you sure you want to delete this payment of ${formatCurrency(paymentAmount)}? This will restore the invoice balance.`)) {
      return
    }

    await deletePayment.mutateAsync(paymentId)
  }

  const invoicePayments = payments.filter(p => 
    p.invoice_ids?.includes(invoice.id)
  )

  const totalPaid = invoicePayments.reduce((sum, p) => sum + p.amount, 0)
  const currentBalance = invoice.balance_due || (invoice.original_amount - totalPaid)
  const lastPayment = invoicePayments.length > 0 
    ? invoicePayments.sort((a, b) => new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime())[0]
    : null

  if (showPaymentForm) {
    return (
      <Modal isOpen={isOpen} onClose={() => setShowPaymentForm(false)} title="Record Payment" size="lg">
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">Invoice:</span> {invoice.invoice_number} • 
            <span className="font-semibold ml-2">Balance Due:</span> {formatCurrency(invoice.balance_due)}
          </p>
        </div>
        <PaymentForm
          initialData={{
            client_id: invoice.client_id,
            customer_id: invoice.customer_id,
            amount: invoice.balance_due,
          }}
          onSubmit={handleCreatePayment}
          onCancel={() => setShowPaymentForm(false)}
          loading={createPayment.isPending}
        />
      </Modal>
    )
  }

  if (isEditing) {
    return (
      <Modal isOpen={isOpen} onClose={() => setIsEditing(false)} title="Edit Invoice" size="lg">
        <InvoiceForm
          customerId={invoice.customer_id}
          initialData={invoice}
          onSubmit={handleUpdate}
          onCancel={() => setIsEditing(false)}
          loading={updateInvoice.isPending}
        />
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invoice Details" size="xl">
      <div className="space-y-6">
        {/* Quick Actions Header */}
        <div className="flex items-center justify-between pb-4 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{invoice.invoice_number}</h2>
            <div className="flex items-center space-x-3 mt-2">
              <Badge>{invoice.status}</Badge>
              <AgingBadge daysPastDue={daysPastDue} />
              {invoice.category && <Badge variant="info">{invoice.category}</Badge>}
            </div>
          </div>
          <div className="flex space-x-2">
            {/* Hide Payment button for QuickBooks invoices - use detected payments flow */}
            {invoice.source !== 'qbo' && (
              <Button onClick={() => setShowPaymentForm(true)}>
                <Plus className="h-4 w-4 mr-2" />Payment
              </Button>
            )}

            {/* Disable Edit/Delete for QuickBooks invoices */}
            {invoice.source === 'qbo' ? (
              <div className="text-sm text-gray-500 italic flex items-center">
                Synced from QuickBooks. Make changes in QuickBooks.
              </div>
            ) : (
              <>
                <Button variant="secondary" onClick={() => setIsEditing(true)}>
                  <Edit className="h-4 w-4 mr-2" />Edit
                </Button>
                <Button variant="danger" onClick={handleDelete} loading={deleteInvoice.isPending}>
                  <Trash2 className="h-4 w-4 mr-2" />Delete
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card>
            <h3 className="text-lg font-semibold mb-4">Invoice Information</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Invoice Date</dt>
                <dd className="text-sm text-gray-900">{formatDate(invoice.invoice_date)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                <dd className="text-sm text-gray-900">{formatDate(invoice.due_date)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Days Past Due</dt>
                <dd className="text-sm text-gray-900">{formatAging(daysPastDue)}</dd>
              </div>
              {invoice.description && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Description</dt>
                  <dd className="text-sm text-gray-900">{invoice.description}</dd>
                </div>
              )}
            </dl>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">Payment Summary</h3>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Original Amount</dt>
                <dd className="text-sm font-semibold text-gray-900">{formatCurrency(invoice.original_amount)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-sm font-medium text-gray-500">Amount Paid</dt>
                <dd className="text-sm text-green-600 font-semibold">{formatCurrency(totalPaid)}</dd>
              </div>
              <div className="flex justify-between pt-3 border-t">
                <dt className="text-sm font-medium text-gray-900">Current Balance</dt>
                <dd className="text-lg font-bold text-gray-900">{formatCurrency(currentBalance)}</dd>
              </div>
              {lastPayment && (
                <div className="flex justify-between pt-3 border-t">
                  <dt className="text-sm font-medium text-gray-500">Last Payment</dt>
                  <dd className="text-sm text-gray-900">{formatDate(lastPayment.payment_date)}</dd>
                </div>
              )}
            </dl>
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">Customer Details</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Customer</dt>
                <dd className="text-sm">
                  <Link
                    to={`/customers/${invoice.customer?.id}`}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {invoice.customer?.name || 'Not provided'}
                  </Link>
                </dd>
              </div>
              {invoice.customer?.contact_person && (
                <div className="flex items-start">
                  <User className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <dt className="text-xs font-medium text-gray-500">Contact Person</dt>
                    <dd className="text-sm text-gray-900 mt-0.5">{invoice.customer.contact_person}</dd>
                  </div>
                </div>
              )}
              {invoice.customer?.phone ? (
                <div className="flex items-start">
                  <Phone className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <dt className="text-xs font-medium text-gray-500">Phone</dt>
                    <dd className="text-sm mt-0.5">
                      <a
                        href={`tel:${invoice.customer.phone}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {invoice.customer.phone}
                      </a>
                    </dd>
                  </div>
                </div>
              ) : (
                <div className="flex items-start">
                  <Phone className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <dt className="text-xs font-medium text-gray-500">Phone</dt>
                    <dd className="text-sm text-gray-400 mt-0.5">Not provided</dd>
                  </div>
                </div>
              )}
              {invoice.customer?.email ? (
                <div className="flex items-start">
                  <Mail className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <dt className="text-xs font-medium text-gray-500">Email</dt>
                    <dd className="text-sm mt-0.5">
                      <a
                        href={`mailto:${invoice.customer.email}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {invoice.customer.email}
                      </a>
                    </dd>
                  </div>
                </div>
              ) : (
                <div className="flex items-start">
                  <Mail className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <dt className="text-xs font-medium text-gray-500">Email</dt>
                    <dd className="text-sm text-gray-400 mt-0.5">Not provided</dd>
                  </div>
                </div>
              )}
              {invoice.customer?.address ? (
                <div className="flex items-start">
                  <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <dt className="text-xs font-medium text-gray-500">Address</dt>
                    <dd className="text-sm text-gray-900 mt-0.5">{invoice.customer.address}</dd>
                  </div>
                </div>
              ) : (
                <div className="flex items-start">
                  <MapPin className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <dt className="text-xs font-medium text-gray-500">Address</dt>
                    <dd className="text-sm text-gray-400 mt-0.5">Not provided</dd>
                  </div>
                </div>
              )}
              {invoice.customer?.contact_preference && (
                <div className="flex justify-between pt-2 border-t">
                  <dt className="text-xs font-medium text-gray-500">Preferred Contact</dt>
                  <dd className="text-sm text-gray-900">
                    <Badge variant="default" className="text-xs">
                      {invoice.customer.contact_preference}
                    </Badge>
                  </dd>
                </div>
              )}
            </dl>
          </Card>
        </div>

        {invoicePayments.length > 0 && (
          <Card>
            <h3 className="text-lg font-semibold mb-4">Payment History ({invoicePayments.length})</h3>
            <div className="space-y-3">
              {invoicePayments.map((payment: any) => (
                <div key={payment.id} className="flex items-center justify-between py-3 border-b last:border-b-0">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-900">{formatDate(payment.payment_date)}</span>
                      <Badge variant="default">{payment.payment_method}</Badge>
                      {payment.applied_to_baseline && <Badge variant="info">Baseline AR</Badge>}
                    </div>
                    {payment.notes && (
                      <p className="text-sm text-gray-600 mt-1">{payment.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-lg font-semibold text-green-600">{formatCurrency(payment.amount)}</div>
                      {payment.collection_fee_owed > 0 && (
                        <div className="text-xs text-gray-500">Fee: {formatCurrency(payment.collection_fee_owed)}</div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeletePayment(payment.id, payment.amount)}
                      loading={deletePayment.isPending}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="flex justify-end pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Modal>
  )
}
